import { useBurnBatchMutation } from "@/api/inventory/burn";
import { useConfirmationDialog } from "@/context/ConfirmationDialog";
import { usePersistentForm } from "@/hooks/usePersistForm";
import {
	ActionIcon,
	Button,
	Group,
	NumberInput,
	Stack,
	Table,
	Text,
} from "@mantine/core";
import { IconReload, IconTrash } from "@tabler/icons-react";
import { useStore } from "@tanstack/react-form";
import { z } from "zod";

const burnEntrySchema = z.object({
	tokenId: z.number().min(0, "Token ID must be positive"),
	amount: z.number().min(1, "Amount must be at least 1"),
});

const formSchema = z.object({
	entries: z.array(burnEntrySchema),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
	entries: [{ tokenId: 0, amount: 1 }],
};

interface BatchBurnProps {
	collectionAddress: string;
}

export function BatchBurn({ collectionAddress }: BatchBurnProps) {
	const burnBatchMutation = useBurnBatchMutation();
	const { confirm } = useConfirmationDialog();

	const form = usePersistentForm(
		`form${window.location.pathname.replace(/\//g, "-")}`,
		{
			defaultValues: DEFAULT_VALUES,
			validators: {
				onSubmit: formSchema,
			},
			onSubmit: async ({ value }) => {
				const confirmed = await confirm(
					"Confirm Burn",
					"Are you sure you want to burn these NFTs?",
					"Burn",
					"Cancel",
				);

				if (!confirmed) {
					throw new Error("Burn cancelled");
				}

				await burnBatchMutation.mutateAsync({
					collectionAddress,
					tokenAmounts: value.entries.map((entry) => ({
						tokenId: entry.tokenId,
						amount: entry.amount,
					})),
				});
			},
		},
	);

	const canSubmit = useStore(form.store, (state) => state.canSubmit);
	const isSubmitting = burnBatchMutation.isPending;
	const isSubmitted = burnBatchMutation.isSuccess;

	const addNewEntry = () => {
		const currentEntries = form.getFieldValue("entries");
		const firstEntry = currentEntries[0];
		const newEntry = {
			tokenId: firstEntry.tokenId + 1,
			amount: firstEntry.amount,
		};
		form.reset({
			entries: [newEntry, ...currentEntries],
		});
	};

	const removeEntry = (index: number) => {
		const currentEntries = form.getFieldValue("entries");
		form.reset({
			entries: currentEntries.filter((_, i) => i !== index),
		});
	};

	return (
		<Stack gap="md" p="md" maw={900} mx="auto">
			<Group justify="space-between">
				<Text size="xl" fw={700}>
					Batch Burn NFTs (ERC1155)
				</Text>
				<Group gap="sm">
					<Button
						variant="subtle"
						leftSection={<IconReload size="1.2rem" />}
						onClick={() => {
							form.reset(DEFAULT_VALUES);
							burnBatchMutation.reset();
						}}
					>
						Reset Form
					</Button>
					<Button
						variant="light"
						onClick={addNewEntry}
						disabled={isSubmitting || isSubmitted}
					>
						Add Another NFT
					</Button>
					{isSubmitted ? (
						<Button
							onClick={() => {
								burnBatchMutation.reset();
							}}
						>
							Burn More
						</Button>
					) : (
						<Button
							onClick={() => form.handleSubmit()}
							loading={isSubmitting}
							disabled={!canSubmit || isSubmitting || isSubmitted}
						>
							Burn NFTs
						</Button>
					)}
				</Group>
			</Group>

			<Table>
				<Table.Thead>
					<Table.Tr style={{ overflowX: "auto", width: "100%" }}>
						<Table.Th>Token ID</Table.Th>
						<Table.Th>Amount</Table.Th>
						<Table.Th style={{ minWidth: "40px", width: "40px" }} />
					</Table.Tr>
				</Table.Thead>
				<Table.Tbody>
					<form.Subscribe selector={(state) => state.values.entries}>
						{(entries) =>
							entries.map((_, index) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								<Table.Tr key={index}>
									<Table.Td>
										<form.Field name={`entries[${index}].tokenId`}>
											{(field) => (
												<NumberInput
													flex={1}
													value={field.state.value}
													onChange={(value) =>
														field.handleChange(Number(value))
													}
													onBlur={field.handleBlur}
													required
													allowDecimal={false}
													min={0}
													placeholder="Token ID"
													step={1}
													thousandSeparator=","
													error={field.state.meta.errors[0]?.toString()}
													disabled={isSubmitting || isSubmitted}
												/>
											)}
										</form.Field>
									</Table.Td>
									<Table.Td>
										<form.Field name={`entries[${index}].amount`}>
											{(field) => (
												<NumberInput
													flex={1}
													value={field.state.value}
													onChange={(value) =>
														field.handleChange(Number(value))
													}
													onBlur={field.handleBlur}
													allowDecimal={false}
													placeholder="Amount"
													min={1}
													step={1}
													thousandSeparator=","
													error={field.state.meta.errors[0]?.toString()}
													disabled={isSubmitting || isSubmitted}
												/>
											)}
										</form.Field>
									</Table.Td>
									<Table.Td>
										<ActionIcon
											color="red"
											variant="subtle"
											onClick={() => removeEntry(index)}
											disabled={
												entries.length === 1 || isSubmitting || isSubmitted
											}
										>
											<IconTrash size="1.2rem" />
										</ActionIcon>
									</Table.Td>
								</Table.Tr>
							))
						}
					</form.Subscribe>
				</Table.Tbody>
			</Table>
		</Stack>
	);
}
